/*
 * cart-subscribe-upgrade.js
 *
 * Wires the cart drawer "upgrade to subscription" module
 * (snippets/cart-subscribe-upgrade.liquid).
 *
 * - Chevron toggles the benefits table. The expanded state lives at module
 *   level so it survives Horizon's section morphing after cart updates.
 * - "Subscribe" converts every convertible line (serialized by Liquid in
 *   `data-lines`) to its selling plan via /cart/change, then dispatches
 *   Horizon's `cart:update` with the rendered sections so the drawer morphs.
 *   The module disappears on its own once no one-time line is left.
 *
 * Feature-scoped: it does NOT touch the core cart web components.
 */
import { ThemeEvents } from '@theme/events';

const state = { expanded: false };

class CartSubscribeUpgrade extends HTMLElement {
  connectedCallback() {
    this.toggleButton = this.querySelector('[data-cart-subscribe-toggle]');
    this.submitButton = this.querySelector('[data-cart-subscribe-submit]');

    this.handleToggle = this.handleToggle.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleCartUpdate = this.handleCartUpdate.bind(this);

    this.toggleButton?.addEventListener('click', this.handleToggle);
    this.submitButton?.addEventListener('click', this.handleSubmit);
    document.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate);

    this.applyExpanded();
  }

  disconnectedCallback() {
    this.toggleButton?.removeEventListener('click', this.handleToggle);
    this.submitButton?.removeEventListener('click', this.handleSubmit);
    document.removeEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate);
  }

  get lines() {
    try {
      const parsed = JSON.parse(this.dataset.lines ?? '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  get sectionIds() {
    const ids = [...document.querySelectorAll('cart-items-component[data-section-id]')]
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);
    return [...new Set(ids)];
  }

  applyExpanded() {
    this.classList.toggle('cart-subscribe--expanded', state.expanded);
    this.toggleButton?.setAttribute('aria-expanded', String(state.expanded));
  }

  handleToggle() {
    state.expanded = !state.expanded;
    this.applyExpanded();
  }

  handleCartUpdate() {
    // Morphing resets attributes from the server HTML; re-apply after it runs.
    requestAnimationFrame(() => {
      if (this.isConnected) this.applyExpanded();
    });
  }

  async handleSubmit() {
    if (this.busy || !this.submitButton) return;

    const lines = this.lines;
    if (!lines.length) return;

    this.busy = true;
    const idleLabel = this.submitButton.textContent;
    this.submitButton.disabled = true;
    this.submitButton.textContent = this.dataset.loadingLabel || idleLabel;

    const route = window.Theme?.routes?.cart_change_url ?? '/cart/change.js';
    const sectionIds = this.sectionIds;
    let sections = null;
    let converted = 0;

    try {
      for (const line of lines) {
        const body = { id: line.key, quantity: line.quantity, selling_plan: line.selling_plan };
        if (sectionIds.length) {
          body.sections = sectionIds.join(',');
          body.sections_url = window.location.pathname;
        }

        const response = await fetch(route, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
          credentials: 'same-origin',
        });
        if (!response.ok) throw new Error(`change failed: ${response.status}`);

        const data = await response.json();
        if (data.errors) throw new Error(data.errors);

        converted += 1;
        sections = data.sections ?? sections;
      }

      if (converted > 0) this.dispatchSubscribed();

      document.dispatchEvent(
        new CustomEvent(ThemeEvents.cartUpdate, {
          bubbles: true,
          detail: {
            resource: {},
            sourceId: 'cart-subscribe-upgrade',
            data: { source: 'cart-subscribe-upgrade', sections },
          },
        })
      );
    } catch (error) {
      console.error('[cart-subscribe-upgrade] upgrade failed', error);
    } finally {
      this.busy = false;
      if (this.isConnected) {
        this.submitButton.disabled = false;
        this.submitButton.textContent = idleLabel;
      }
    }
  }

  dispatchSubscribed() {
    const name = this.dataset.event;
    if (!name) return;
    document.dispatchEvent(new CustomEvent(name, { bubbles: true }));
    if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event: name });
  }
}

if (!customElements.get('cart-subscribe-upgrade')) {
  customElements.define('cart-subscribe-upgrade', CartSubscribeUpgrade);
}
